import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@airclou/ui';
import { AlertTriangle } from 'lucide-react';

interface RefreshErrorBannerProps {
  /** What failed, in the server's words where we have them. */
  message: string;
  onRetry: () => void;
  /** Show the retry as in progress, so a slow retry isn't invisible. */
  isRetrying?: boolean;
}

/**
 * A reload failed while the page still holds good data: keep the page, say so
 * inline, and offer a retry. The full-page `ErrorState` is for the other case —
 * the record is gone and the page has nothing left to show.
 *
 * Lives beside `ErrorState` rather than in `packages/ui` for the same reason
 * that one does: the retry label comes from this app's `dashboard` i18n
 * namespace. Promote both together if another app needs the pair.
 *
 * Literal reds with explicit dark steps: this means error, so it must not
 * follow the brand, and the `destructive` token is a fill colour whose dark
 * value is too dark to read as text.
 */
export function RefreshErrorBanner({ message, onRetry, isRetrying = false }: RefreshErrorBannerProps) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="flex items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/40 dark:bg-red-900/20">
      <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span>{message}</span>
      </div>
      <LoadingButton variant="outline" size="sm" onClick={onRetry} loading={isRetrying}>
        {t('errorState.tryAgain')}
      </LoadingButton>
    </div>
  );
}
